import React from 'react';
import { type StudioPageContent, type StudioWatermarkLogoId } from '../../../../constants';
import { colorWithAlpha, isDarkColor, mixColor } from '../../../../utils/color';
import { STUDIO_FONT_SC, STUDIO_FONT_TC } from '../../../../utils/fonts';
import { CanvasLogoWatermark } from '../../../LogoWatermark';
import StudioPageNumber from '../../StudioPageNumber';

export interface EditorialTokens {
  ink: string;
  muted: string;
  accent: string;
  paper: string;
  panel: string;
  line: string;
  serif: string;
  sans: string;
}

export interface EditorialInjectedProps {
  tokens?: EditorialTokens;
  isStory?: boolean;
  locale?: string;
  qrCodeUrl?: string;
}

export interface EditorialLayoutProps {
  width: number;
  height: number;
  format: string;
  locale?: string;
  content?: StudioPageContent;
  themeColor?: string;
  profileName?: string;
  pageNumber?: number;
  pageCount?: number;
  watermarkLogo?: StudioWatermarkLogoId | null;
  showChubbDisclaimer?: boolean;
  qrCodeUrl?: string;
  pinContentTop?: boolean;
}

interface EditorialFrameProps extends EditorialLayoutProps {
  children: React.ReactElement;
}

const EditorialFrame: React.FC<EditorialFrameProps> = ({
  width,
  height,
  format,
  locale = 'zh-Hant-HK',
  themeColor = '#1456f0',
  profileName,
  pageNumber = 1,
  pageCount = 7,
  watermarkLogo,
  showChubbDisclaimer = false,
  qrCodeUrl,
  pinContentTop = false,
  children
}) => {
  const isStory = format === 'story';
  const accent = isDarkColor(themeColor) ? themeColor : mixColor(themeColor, 'black', 0.3);
  const tokens: EditorialTokens = {
    ink: '#18201d',
    muted: '#64665f',
    accent,
    paper: '#f5f0e6',
    panel: 'rgba(255, 253, 247, 0.78)',
    line: colorWithAlpha(accent, 0.24),
    serif: "Georgia, 'Noto Serif TC', 'Noto Serif SC', 'Songti TC', 'Songti SC', serif",
    sans: `'DM Sans', '${STUDIO_FONT_TC}', '${STUDIO_FONT_SC}', -apple-system, BlinkMacSystemFont, sans-serif`
  };
  const note = profileName && String(profileName).trim() ? String(profileName).trim() : '';

  return (
    <div
      style={{
        width,
        height,
        position: 'relative',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        boxSizing: 'border-box',
        color: tokens.ink,
        background: `linear-gradient(145deg, ${tokens.paper} 0%, ${mixColor(themeColor, 'white', 0.94)} 100%)`,
        fontFamily: tokens.sans,
        // Pin alignment so the page renders identically no matter what the host
        // is (the picker cover lives inside a MUI Button whose text-align:center
        // would otherwise centre the text).
        textAlign: 'left'
      }}
    >
      <CanvasLogoWatermark logo={watermarkLogo} color="#000000" isStory={isStory} />
      <StudioPageNumber
        pageNumber={pageNumber}
        pageCount={pageCount}
        color={tokens.accent}
        isStory={isStory}
      />

      <header
        style={{
          position: 'relative',
          zIndex: 1,
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-start',
          // Page number is absolute now — no need for a wide right margin that
          // used to clear the in-header "01 / 07", so the top rule can run longer.
          margin: isStory ? '31px 28px 0 28px' : '23px 24px 0 24px',
          paddingBottom: isStory ? 11 : 9,
          borderBottom: `1px solid ${tokens.accent}`,
          color: tokens.accent,
          fontSize: isStory ? 10 : 8.5,
          fontWeight: 800
        }}
      >
        <span
          style={{
            maxWidth: isStory ? 220 : 190,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            letterSpacing: 0.3,
            textTransform: 'none'
          }}
        >
          {note}
        </span>
      </header>

      <main
        style={{
          position: 'relative',
          zIndex: 1,
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          padding: isStory
            ? `30px 31px ${pinContentTop ? 116 : showChubbDisclaimer ? 52 : 24}px 31px`
            : `22px 27px ${pinContentTop ? 80 : showChubbDisclaimer ? 45 : 16}px 27px`,
          boxSizing: 'border-box',
          overflowWrap: 'anywhere'
        }}
      >
        {React.cloneElement(children as React.ReactElement<any>, { tokens, isStory, locale, qrCodeUrl })}
      </main>
    </div>
  );
};

export default EditorialFrame;
